import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import blackFront from '../../assets/images/black-front.jpg';
import blackBack from '../../assets/images/black-back.jpg';
import blackLeft from '../../assets/images/black-left.png';
import blackRight from '../../assets/images/black-right.png';

import blueFront from '../../assets/images/blue-front.png';
import blueBack from '../../assets/images/blue-back.jpg';
import blueLeft from '../../assets/images/blue-left.png';
import blueRight from '../../assets/images/blue-right.png';

import limeFront from '../../assets/images/lime-front.jpg';
import limeBack from '../../assets/images/lime-back.jpg';
import limeLeft from '../../assets/images/lime-left.png';
import limeRight from '../../assets/images/lime-right.png';

import skyFront from '../../assets/images/sky-front.jpg';
import skyBack from '../../assets/images/sky-back.jpg';
import skyLeft from '../../assets/images/sky-left.png';
import skyRight from '../../assets/images/sky-right.png';
import { buildImageFilter, getCurvedTextMetrics } from './DesignOverlay';
import '../../assets/css/placement.css';
import { Expand, Move, Trash2 } from 'lucide-react';

// Curved text needs its own fit-to-area logic because the shared
// straight-text auto-fit (autoFitText, below) works by measuring a plain
// HTML element's scrollWidth/scrollHeight — meaningless here since the
// visible glyphs live inside an SVG <text> whose size comes from the
// `fontSize` attribute, not inherited CSS. Instead this component:
//  1) builds the arc so it's always at least as wide as the real
//     (canvas-measured) text — this is what fixes text getting clipped
//     at the start/end when curved, regardless of font size or length.
//  2) measures the actual rendered bounding box via getBBox(), and if it
//     doesn't fit within the available print-area space (maxWidth /
//     maxHeight, passed down from the parent), shrinks the persisted
//     fontSize and lets the next render re-measure — converges in 1-2
//     renders since the scale factor is computed directly from the
//     overflow ratio rather than stepping down one px at a time.
const CurvedText = ({ t, displayText, maxWidth, maxHeight, onFitFontSize, setBoxSize, disableFit = false }) => {
  const textRef = useRef(null);
  const lastFitRef = useRef({ fontSize: null, text: null, maxWidth: null, maxHeight: null });
  const isDraggingRef = useRef(false);
  const lastBoxRef = useRef(null);
  const lastBoxSizeRef = useRef(null);

  const [box, setBox] = useState({
    width: 200,
    height: 100,
    x: 0,
    y: 0,
  });

  const pathId = `curve-${t.id}`;
  const curveText = displayText.replace(/\n/g, "");
  const fontSize = t.fontSize || 16;
  const arcDirection = t.arcDirection || 'up';

  // ✅ FIX: Add letter spacing ONLY for downward arcs (not padding multiplier)
  const letterSpacing = arcDirection === 'down' ? '0.09em' : 'normal';

  const { pathD } = getCurvedTextMetrics(curveText, {
    fontSize,
    fontFamily: t.fontFamily || 'Arial',
    bold: !!t.bold,
    italic: !!t.italic,
    curveIntensity: t.curveIntensity || 100,
    direction: arcDirection,
  });

  useLayoutEffect(() => {
    if (!textRef.current) return;

    const textPathEl = textRef.current.querySelector('textPath');
    if (!textPathEl) return;

    let bbox;
    try {
      bbox = textPathEl.getBBox();
    } catch (e) {
      console.log('getBBox failed, using fallback measurements');
      bbox = {
        x: 0,
        y: -fontSize * 0.3,
        width: curveText.length * fontSize * 0.6,
        height: fontSize * 1.2,
      };
    }

    // ✅ FIXED: Use SAME padding for both directions
    // Don't multiply based on direction - that's what caused the width issue
    const padding = 1;
    const verticalPadding = 0;

    const adjustedBox = {
      width: Math.max(bbox.width, curveText.length * fontSize * 0.55) + padding * 2,
      height: bbox.height + verticalPadding * 2,
      x: bbox.x - padding,
      y: bbox.y - verticalPadding,
    };

    // For downward arcs, add extra vertical space (but NOT horizontal width)
    if (arcDirection === 'down') {
      adjustedBox.y = bbox.y - verticalPadding;
      adjustedBox.height = bbox.height + verticalPadding * 2;
    }

    // Ensure minimum dimensions based on text content
    const minWidth = Math.max(curveText.length * fontSize * 0.55, 20);
    const minHeight = Math.max(fontSize * 1.2, 20);
    adjustedBox.width = Math.max(adjustedBox.width, minWidth);
    adjustedBox.height = Math.max(adjustedBox.height, minHeight);

    // Center the box around the text bounding box
    const centerX = adjustedBox.x + adjustedBox.width / 2;
    const centerY = adjustedBox.y + adjustedBox.height / 2;
    adjustedBox.x = centerX - adjustedBox.width / 2;
    adjustedBox.y = centerY - adjustedBox.height / 2;

    // Only update box state if values actually changed to prevent unnecessary re-renders
    const lastBox = lastBoxRef.current;
    if (
      !lastBox ||
      lastBox.width !== adjustedBox.width ||
      lastBox.height !== adjustedBox.height ||
      lastBox.x !== adjustedBox.x ||
      lastBox.y !== adjustedBox.y
    ) {
      lastBoxRef.current = { ...adjustedBox };
      setBox(adjustedBox);
    }

    if (typeof setBoxSize === 'function') {
      const lastBoxSize = lastBoxSizeRef.current;
      if (
        !lastBoxSize ||
        lastBoxSize.width !== adjustedBox.width ||
        lastBoxSize.height !== adjustedBox.height
      ) {
        lastBoxSizeRef.current = { width: adjustedBox.width, height: adjustedBox.height };
        setBoxSize(lastBoxSizeRef.current);
      }
    }

    // Auto-shrink to fit the print area
    if (
      isDraggingRef.current ||
      disableFit ||
      typeof onFitFontSize !== 'function' ||
      maxWidth <= 0 ||
      maxHeight <= 0 ||
      (adjustedBox.width <= maxWidth && adjustedBox.height <= maxHeight)
    ) {
      return;
    }

    const scale = Math.min(maxWidth / adjustedBox.width, maxHeight / adjustedBox.height);
    const nextFontSize = Math.max(8, Math.floor(fontSize * scale * 0.98));

    const alreadyTried =
      lastFitRef.current.fontSize === nextFontSize &&
      lastFitRef.current.text === curveText &&
      lastFitRef.current.maxWidth === maxWidth &&
      lastFitRef.current.maxHeight === maxHeight;

    if (nextFontSize < fontSize && !alreadyTried) {
      lastFitRef.current = { fontSize: nextFontSize, text: curveText, maxWidth, maxHeight };
      onFitFontSize(t.id, nextFontSize);
    }
  }, [displayText, fontSize, t.fontFamily, t.bold, t.italic, t.curveIntensity, arcDirection, maxWidth, maxHeight]);

  return (
    <svg
      width={box.width}
      height={box.height}
      viewBox={`${box.x} ${box.y} ${box.width} ${box.height}`}
      overflow="visible"
      style={{
        display: 'inline-block',
        verticalAlign: 'baseline',
        minWidth: box.width,
        minHeight: box.height,
      }}
    >
      <defs>
        <path id={pathId} d={pathD} fill="none" />
      </defs>

      <text
        ref={textRef}
        fontSize={fontSize}
        fontFamily={t.fontFamily}
        fill={t.color}
        fontWeight={t.bold ? 700 : 400}
        fontStyle={t.italic ? "italic" : "normal"}
        textDecoration={t.underline ? "underline" : "none"}
        stroke={t.strokeColor}
        strokeWidth={t.strokeWidth}
        // ✅ Apply letter spacing to fix cramped text on downward curves
        letterSpacing={letterSpacing}
      >
        <textPath
          href={`#${pathId}`}
          startOffset="50%"
          textAnchor="middle"
        // method="stretch"
        // spacing="auto"
        >
          {displayText.replace(/\n/g, " ")}
        </textPath>
      </text>
    </svg>
  );
};

const TShirtPlacement = ({
  activePlacementKey,
  setActivePlacementKey,
  placementTexts,
  setPlacementTexts,
  placementImages,
  setPlacementImages,
  placementList,
  setPlacementList,
  activeTextId,
  setActiveTextId,
  selectedColors,
  customizerRef,
  setSelectedTextId,
  setSelectedImageId,
  selectedTextId,
  setActiveTab,
  placementMethods,
  setPlacementMethods,
  selectedPlacement,
  setSelectedPlacement,
  methodPositions = {},
  setMethodPositions,
  overlayCenter,
  setOverlayCenter,
  activeImageId,
  setActiveImageId,
  setBoxSize
}) => {
  const imageItems =
    (activePlacementKey && placementImages && placementImages[activePlacementKey])
      ? placementImages[activePlacementKey]
      : [];

  const placements = useMemo(
    () => [
      { id: 'front', label: 'Front', icon: blackFront },
      { id: 'back', label: 'Back', icon: blackBack },
      { id: 'left', label: 'Left', icon: blackLeft },
      { id: 'right', label: 'Right', icon: blackRight },
    ],
    []
  );

  const methods = useMemo(
    () => [
      { id: 'print', label: 'Print' },
      { id: 'embroidery', label: 'Embroidery' },
    ],
    []
  );

  const shirtImagesByColor = {
    '#000000': { front: blackFront, back: blackBack, left: blackLeft, right: blackRight },
    '#99DFF9': { front: skyFront, back: skyBack, left: skyLeft, right: skyRight },
    '#0255A1': { front: blueFront, back: blueBack, left: blueLeft, right: blueRight },
    '#85CD6A': { front: limeFront, back: limeBack, left: limeLeft, right: limeRight },
  };

  const currentShirtImages = useMemo(() => {
    const selected = Array.isArray(selectedColors) ? selectedColors[0] : null;
    return shirtImagesByColor[selected] || shirtImagesByColor['#000000'];
  }, [selectedColors]);

  const shirtImages = currentShirtImages;

  const [isRemoveWhiteLoader, setIsRemoveWhiteLoader] = useState(false)
  const [resizingActiveId, setResizingActiveId] = useState(null);
  const overlayRef = useRef(null);
  const textRefs = useRef({});
  const imageRefs = useRef({});

  const dragDataRef = useRef({
    type: null,
    imageId: null,
    textId: null,
    startClientX: 0,
    startClientY: 0,
    startX: 0,
    startY: 0,
  });
  
  const resizingSelectionRef = useRef(null);
  const suppressNextClickRef = useRef(false);

  const resizeRef = useRef({
    resizing: false,
    type: null, // "image" | "text"
    imageId: null,
    textId: null,
    direction: null, // 'top' | 'bottom' | 'left' | 'right'
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0,
    startXPos: 0,
    startYPos: 0,
    startFontSize: 0,
  });

  const rotateRef = useRef({
    rotating: false,
    type: null, // "image" | "text"
    id: null,
    centerX: 0,
    centerY: 0,
    lastAngle: 0,       // angle measured on the previous pointermove frame
    currentRotation: 0, // running rotation total, updated incrementally
  });

  const formatLabel = (placementId, methodId) => {
    const p = placements.find((x) => x.id === placementId)?.label ?? placementId;
    const m = methodId.charAt(0).toUpperCase() + methodId.slice(1);
    return `${p} ${m}`;
  };

  const [activeView, setActiveView] = useState('front'); // front/back/left/right

  // Keep the preview view (front/back/left/right) in sync with the
  // selected placement key coming from parent state.
  useEffect(() => {
    if (!activePlacementKey || typeof activePlacementKey !== 'string') return;

    if (activePlacementKey.startsWith('front-')) return setActiveView('front');
    if (activePlacementKey.startsWith('back-')) return setActiveView('back');
    if (activePlacementKey.startsWith('left-')) return setActiveView('left');
    if (activePlacementKey.startsWith('right-')) return setActiveView('right');
  }, [activePlacementKey]);

  // Keep the on-canvas preview view (front/back/left/right) in sync with the
  // currently selected placement so the t-shirt preview shows Front (with its
  // selected method) whenever Front is selected.
  useEffect(() => {
    setActiveView(selectedPlacement);
  }, [selectedPlacement]);

  const currentMethod = placementMethods[selectedPlacement] ?? '';


  const methodPos = methodPositions?.[activePlacementKey] || { x: 0, y: 0 };
  const methodDrag = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    mouseX: 0,
    mouseY: 0,
  });

  const embroideryBounds = {
    front: {
      minX: -180,
      maxX: 30,
      minY: -80,
      maxY: 250,
    },
    back: {
      minX: -55,
      maxX: 50,
      minY: 15,
      maxY: 250,
    },
  };

  const PRINT_AREA_INCHES = {
    front: { print: { width: 12, height: 16 }, embroidery: { width: 4, height: 4 } },
    back: { print: { width: 12, height: 16 }, embroidery: { width: 4, height: 4 } },
    left: { print: { width: 4, height: 4 }, embroidery: { width: 4, height: 4 } },
    right: { print: { width: 4, height: 4 }, embroidery: { width: 4, height: 4 } },
  };

  const getPxPerInch = () => {
    const dims = getOverlayDims();
    const areaInches = PRINT_AREA_INCHES[selectedPlacement]?.[currentMethod];
    if (!dims || !areaInches || !areaInches.width || !areaInches.height) return null;

    return {
      x: dims.width / areaInches.width,
      y: dims.height / areaInches.height,
    };
  };

  const pxToInches = (px, axis = 'x') => {
    const ratio = getPxPerInch();
    if (!ratio || !ratio[axis]) return null;
    return px / ratio[axis];
  };

  const formatInchesLabel = (widthPx, heightPx) => {
    const wIn = pxToInches(widthPx, 'x');
    const hIn = pxToInches(heightPx, 'y');
    if (wIn == null || hIn == null) return null;
    return `${wIn.toFixed(2)} in X ${hIn.toFixed(2)} in`;
  };

  const InchesBadge = ({ widthPx, heightPx, active, isImage = false, imageSrc = '', setIsRemoveWhiteLoader }) => {

    const label = formatInchesLabel(widthPx, heightPx);
    if (!label) return null;

    const removeWhiteBackground = async (imageSrc) => {
      // Keep the currently selected image ID
      const currentImageId = activeImageId;

      if (!currentImageId || !imageSrc || !activePlacementKey) {
        return;
      }

      try {
        setIsRemoveWhiteLoader(true)
        const img = new Image();

        img.crossOrigin = "anonymous";

        const processedImage = await new Promise((resolve, reject) => {
          img.onload = () => {
            try {
              const canvas = document.createElement("canvas");
              const ctx = canvas.getContext("2d");

              if (!ctx) {
                reject(new Error("Canvas context not available"));
                return;
              }

              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;

              ctx.drawImage(img, 0, 0);

              const imageData = ctx.getImageData(
                0,
                0,
                canvas.width,
                canvas.height
              );

              const data = imageData.data;

              for (let i = 0; i < data.length; i += 4) {
                const r = data[i];
                const g = data[i + 1];
                const b = data[i + 2];

                if (r > 240 && g > 240 && b > 240) {
                  data[i + 3] = 0;
                }
              }

              ctx.putImageData(imageData, 0, 0);

              resolve(canvas.toDataURL("image/png"));
            } catch (error) {
              reject(error);
            }
          };

          img.onerror = reject;
          img.src = imageSrc;
        });

        // Update only imageDataUrl.
        // Do NOT create a new image object or change its id/position/size.
        setPlacementImages((prev) => ({
          ...prev,
          [activePlacementKey]: (prev[activePlacementKey] || []).map((image) =>
            image.id === currentImageId
              ? {
                ...image,
                imageDataUrl: processedImage,
              }
              : image
          ),
        }));

        // Explicitly restore/keep selection
        setActiveImageId(currentImageId);
        setSelectedImageId?.(currentImageId);

        // Keep text deselected
        setActiveTextId?.(null);
        setSelectedTextId?.(null);

        setActiveTab?.("images");
        setIsRemoveWhiteLoader(false)
      } catch (error) {
        console.error("Failed to remove white background:", error);

        // Even if processing fails, keep the image selected
        setActiveImageId(currentImageId);
        setSelectedImageId?.(currentImageId);
        setIsRemoveWhiteLoader(false)
      }
    };

    return (
      <>
        <div
          style={{
            position: 'absolute',
            left: '50%',
            bottom: -22,
            transform: 'translateX(-50%)',
            background: 'transparent',
            color: '#05f',
            fontSize: 11,
            fontWeight: 700,
            padding: '3px 8px',
            borderRadius: 4,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 30,
          }}
        >
          {label}
        </div>
        {
          isImage &&
          <button
            style={{
              position: 'absolute',
              left: '50%',
              bottom: -38,
              transform: 'translateX(-50%)',
              background: 'transparent',
              color: '#666',
              fontSize: 11,
              whiteSpace: 'nowrap',
              pointerEvents: 'auto',
              userSelect: 'none',
              zIndex: 99,
              cursor: 'pointer',
              border: 0,
              backgroundColor: 'white',
              padding: '2px 8px'
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}

            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              removeWhiteBackground(imageSrc);
            }}
          >
            Remove White
          </button>
        }
      </>
    );
  };

  const handleMethodPointerDown = (e) => {

    if (
      currentMethod !== "embroidery" ||
      !(activePlacementKey?.startsWith("front-") ||
        activePlacementKey?.startsWith("back-"))
    ) {
      return;
    }

    // Don't start if a child (text/image) was clicked
    if (e.target !== e.currentTarget) return;

    if (e.target !== overlayRef.current) return;

    const pos = methodPositions[activePlacementKey] || {
      x: 0,
      y: 0
    };

    methodDrag.current = {
      dragging: true,
      startX: pos.x,
      startY: pos.y,
      mouseX: e.clientX,
      mouseY: e.clientY,
    };

    overlayRef.current.setPointerCapture(e.pointerId);
  };

  const handleMethodPointerMove = (e) => {

    if (!methodDrag.current.dragging) return;

    const dx = e.clientX - methodDrag.current.mouseX;
    const dy = e.clientY - methodDrag.current.mouseY;

    const bounds = embroideryBounds[selectedPlacement];

    let nextX = methodDrag.current.startX + dx;
    let nextY = methodDrag.current.startY + dy;

    if (bounds) {
      nextX = Math.max(bounds.minX, Math.min(bounds.maxX, nextX));
      nextY = Math.max(bounds.minY, Math.min(bounds.maxY, nextY));
    }

    if (typeof setMethodPositions === 'function') {
      setMethodPositions((prev) => ({
        ...prev,
        [activePlacementKey]: {
          x: nextX,
          y: nextY,
        },
      }));
    }
  };

  const handleMethodPointerUp = () => {
    methodDrag.current.dragging = false;
  };

  // NEW: dedicated "move" handle for embroidery placement
  const handleMoveHandlePointerDown = (e) => {
    e.stopPropagation();

    if (
      currentMethod !== "embroidery" ||
      !(activePlacementKey?.startsWith("front-") ||
        activePlacementKey?.startsWith("back-"))
    ) {
      return;
    }

    const pos = methodPositions[activePlacementKey] || { x: 0, y: 0 };

    methodDrag.current = {
      dragging: true,
      startX: pos.x,
      startY: pos.y,
      mouseX: e.clientX,
      mouseY: e.clientY,
    };
    overlayRef.current?.setPointerCapture(e.pointerId);
  };

  // Handle adding placement
  const handleAddPlacement = (placement, method) => {

    const placementKey = `${placement}-${method}`;
    setActivePlacementKey(placementKey)

    setPlacementMethods((prev) => ({
      ...prev,
      [placement]: method,
    }));

    setPlacementList((prev) => {
      const filtered = prev.filter(
        (item) => item.placement !== placement
      );

      return [
        ...filtered,
        {
          key: placementKey,
          placement,
          method,
          label: formatLabel(placement, method),
        },
      ];
    });

    setActivePlacementKey(placementKey);
    setSelectedPlacement(placement);
    setActiveView(placement);
  };

  // --- Boundary clamping helpers -------------------------------------
  // Keeps any element (text or image) fully inside the current design
  // (overlay) area. Used on every drag / resize update instead of
  // detecting overflow after the fact.
  const getOverlayDims = () => {
    const overlay = overlayRef.current;
    if (!overlay) return null;
    return { width: overlay.clientWidth, height: overlay.clientHeight };
  };

  const clampPosition = (x, y, width, height) => {
    const dims = getOverlayDims();
    if (!dims) return { x, y };

    const maxX = Math.max(0, dims.width - width);
    const maxY = Math.max(0, dims.height - height);

    return {
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    };
  };

  const clampSize = (x, y, width, height, minSize = 30) => {
    const dims = getOverlayDims();
    if (!dims) return { width: Math.max(minSize, width), height: Math.max(minSize, height) };

    const safeDimsWidth = Math.max(0, dims.width);
    const safeDimsHeight = Math.max(0, dims.height);

    const maxWidth = Math.max(minSize, safeDimsWidth - x);
    const maxHeight = Math.max(minSize, safeDimsHeight - y);

    return {
      width: Math.max(minSize, Math.min(maxWidth, width)),
      height: Math.max(minSize, Math.min(maxHeight, height)),
    };
  };
  const getTextTransformMatrix = (t) => {
    const toRad = (deg) => ((deg || 0) * Math.PI) / 180;
    const r = toRad(t.rotation || 0);
    const hF = t.flipH ? -1 : 1;
    const vF = t.flipV ? -1 : 1;
    const ta = Math.tan(toRad(t.skewX || 0));
    const tb = Math.tan(toRad(t.skewY || 0));
    const c = Math.cos(r);
    const s = Math.sin(r);

    const A = c * hF * (1 + ta * tb) - s * vF * tb;
    const B = c * hF * ta - s * vF;
    const C = s * hF * (1 + ta * tb) + c * vF * tb;
    const D = s * hF * ta + c * vF;

    return { A, B, C, D };
  };

  const getTextVisualBounds = (t, w, h) => {
    const { A, B, C, D } = getTextTransformMatrix(t);

    // For a w x h rectangle centered on the transform origin, the
    // transformed axis-aligned bounding box is:
    //   visualW = |A| * w + |B| * h
    //   visualH = |C| * w + |D| * h
    const visualW = Math.abs(A) * w + Math.abs(B) * h;
    const visualH = Math.abs(C) * w + Math.abs(D) * h;

    // Drop shadow offset is painted in the element's local space, so it is
    // transformed by the same matrix. Extend bounds by the offset + blur.
    const ox = t.shadowOffsetX || 0;
    const oy = t.shadowOffsetY || 0;
    const hasShadow = !!ox || !!oy;
    const blur = hasShadow ? 4 : 0;
    const shx = hasShadow ? A * ox + B * oy : 0;
    const shy = hasShadow ? C * ox + D * oy : 0;
    const shLeft = hasShadow ? Math.max(0, -shx) : 0;
    const shRight = hasShadow ? Math.max(0, shx) : 0;
    const shTop = hasShadow ? Math.max(0, -shy) : 0;
    const shBottom = hasShadow ? Math.max(0, shy) : 0;

    const align = t.textAlign || 'left';
    const alignShift = w * 0.08;

    let extraLeft = 0;
    let extraRight = 0;

    if (align === 'left') {
      extraLeft = alignShift;
    } else if (align === 'right') {
      extraRight = alignShift;
    }

    return {
      offsetLeft: visualW / 2 - w / 2 + shLeft + blur + extraLeft,
      offsetRight: visualW / 2 - w / 2 + shRight + blur + extraRight,
      offsetTop: visualH / 2 - h / 2 + shTop + blur,
      offsetBottom: visualH / 2 - h / 2 + shBottom + blur,
      visualW,
      visualH,
    };
  };

  const clampTextPosition = (rawX, rawY, t, w, h) => {
    const dims = getOverlayDims();
    if (!dims || !w || !h) return { x: rawX, y: rawY };

    const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getTextVisualBounds(t, w, h);
    const maxX = Math.max(0, dims.width - w - offsetRight);
    const maxY = Math.max(0, dims.height - h - offsetBottom);

    return {
      x: Math.min(Math.max(offsetLeft, rawX), maxX),
      y: Math.min(Math.max(offsetTop, rawY), maxY),
    };
  };

  const getImageTransformMatrix = (imgItem) => {
    const toRad = (deg) => ((deg || 0) * Math.PI) / 180;
    const r = toRad(imgItem.rotation || 0);
    const ta = Math.tan(toRad(imgItem.skewX || 0));
    const tb = Math.tan(toRad(imgItem.skewY || 0));
    const c = Math.cos(r);
    const s = Math.sin(r);

    const A = c * (1 + ta * tb) - s * tb;
    const B = c * ta - s;
    const C = s * (1 + ta * tb) + c * tb;
    const D = s * ta + c;

    return { A, B, C, D };
  };

  const getImageVisualBounds = (imgItem, w, h) => {
    const { A, B, C, D } = getImageTransformMatrix(imgItem);

    const visualW = Math.abs(A) * w + Math.abs(B) * h;
    const visualH = Math.abs(C) * w + Math.abs(D) * h;

    return {
      offsetLeft: visualW / 2 - w / 2,
      offsetRight: visualW / 2 - w / 2,
      offsetTop: visualH / 2 - h / 2,
      offsetBottom: visualH / 2 - h / 2,
      visualW,
      visualH,
    };
  };

  const clampImagePosition = (rawX, rawY, imgItem, w, h) => {
    const dims = getOverlayDims();
    if (!dims || !w || !h) return { x: rawX, y: rawY };

    const hasTransform = (imgItem.skewX || 0) !== 0 || (imgItem.skewY || 0) !== 0 || (imgItem.rotation || 0) !== 0;

    if (!hasTransform) {
      return {
        x: Math.min(Math.max(0, rawX), Math.max(0, dims.width - w)),
        y: Math.min(Math.max(0, rawY), Math.max(0, dims.height - h)),
      };
    }

    const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getImageVisualBounds(imgItem, w, h);
    const maxX = Math.max(0, dims.width - w - offsetRight);
    const maxY = Math.max(0, dims.height - h - offsetBottom);

    return {
      x: Math.min(Math.max(offsetLeft, rawX), maxX),
      y: Math.min(Math.max(offsetTop, rawY), maxY),
    };
  };

  const getMaxTextScale = (t, w, h) => {
    const dims = getOverlayDims();
    if (!dims || !w || !h) return 1;

    const x = t.x || 0;
    const y = t.y || 0;
    const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getTextVisualBounds(t, w, h);

    let scale = Infinity;
    if (offsetLeft > 0) scale = Math.min(scale, x / offsetLeft);
    if (offsetTop > 0) scale = Math.min(scale, y / offsetTop);
    scale = Math.min(scale, (dims.width - x) / (w + offsetRight));
    scale = Math.min(scale, (dims.height - y) / (h + offsetBottom));

    return Math.max(0.01, scale);
  };

  const updateImageSize = (id, width, height) => {

    setPlacementImages(prev => ({

      ...prev,

      [activePlacementKey]:

        prev[activePlacementKey].map(img =>

          img.id === id
            ? {
              ...img,
              width,
              height
            }
            : img
        )

    }));

  };

  const updateImagePosition = (imageId, x, y) => {
    setPlacementImages(prev => ({
      ...prev,
      [activePlacementKey]:
        prev[activePlacementKey].map(img =>
          img.id === imageId
            ? {
              ...img,
              x,
              y
            }
            : img
        )
    }));

  };

  const updateImageRotation = (id, rotation) => {
    setPlacementImages(prev => ({
      ...prev,
      [activePlacementKey]:
        prev[activePlacementKey].map(img =>
          img.id === id
            ? { ...img, rotation }
            : img
        )
    }));
  };

  const removeImage = (id) => {
    setPlacementImages(prev => ({
      ...prev,
      [activePlacementKey]:
        (prev[activePlacementKey] || []).filter(img => img.id !== id)
    }));
    setActiveImageId(null);
  };

  const removeText = (textId) => {
    if (!activePlacementKey) return;
    if (!Array.isArray(placementTexts?.[activePlacementKey])) return;
    if (typeof setPlacementTexts !== 'function') return;

    setPlacementTexts((prev) => {
      const items = Array.isArray(prev?.[activePlacementKey]) ? prev[activePlacementKey] : [];
      return {
        ...prev,
        [activePlacementKey]: items.filter((t) => t.id !== textId),
      };
    });
    setSelectedTextId(null);
    setSelectedImageId(null);
  };

  // Handle removing placement
  const handleRemovePlacement = (key) => {
    const itemToRemove = placementList.find((item) => item.key === key);

    if (!itemToRemove) return;

    // Remove from placement list
    const nextList = placementList.filter((item) => item.key !== key);
    setPlacementList(nextList);

    // Clear placement method
    setPlacementMethods((prev) => ({
      ...prev,
      [itemToRemove.placement]: null,
    }));
  };

  const resizePointerDown = (e, img, direction = "right") => {
    e.stopPropagation();

    suppressNextClickRef.current = true;

    overlayRef.current?.setPointerCapture(e.pointerId);

    setActiveImageId(img.id);
    setSelectedImageId?.(img.id);

    setActiveTextId?.(null);
    setSelectedTextId?.(null);

    setResizingActiveId(img.id);
    setActiveTab?.("images");

    resizeRef.current = {
      resizing: true,
      type: "image",
      imageId: img.id,
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: img.width || 150,
      startHeight: img.height || 150,
      startXPos: img.x || 0,
      startYPos: img.y || 0,
    };
  };

  const textResizePointerDown = (e, t) => {
    e.stopPropagation();

    suppressNextClickRef.current = true;

    overlayRef.current?.setPointerCapture(e.pointerId);

    // Keep text selected
    setSelectedTextId(t.id);
    setActiveTextId?.(t.id);

    // Deselect image
    setActiveImageId(null);
    setSelectedImageId?.(null);

    setResizingActiveId(t.id);
    setActiveTab?.(t.role ? "number" : "text");

    resizeRef.current = {
      resizing: true,
      type: "text",
      textId: t.id,
      startX: e.clientX,
      startY: e.clientY,
      startFontSize: t.fontSize || 16,
    };
  };

  const updateTextRotationNormalized = (textId, rotation) => {
    updateTextRotation(textId, normalizeRotation(rotation));
  };

  const updateImageRotationNormalized = (imageId, rotation) => {
    updateImageRotation(imageId, normalizeRotation(rotation));
  };

  const imageRotatePointerDown = (e, img) => {
    e.stopPropagation();

    // Keep image selected while rotating
    suppressNextClickRef.current = true;
    setActiveImageId(img.id);
    setSelectedImageId?.(img.id);

    // Remove text selection
    setActiveTextId?.(null);
    setSelectedTextId?.(null);

    setActiveTab?.('images');

    // KEEP your existing pointer capture
    overlayRef.current?.setPointerCapture(e.pointerId);

    const el = imageRefs.current[img.id];
    if (!el) return;

    const rect = el.getBoundingClientRect();

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const startAngle =
      Math.atan2(
        e.clientY - centerY,
        e.clientX - centerX
      ) * (180 / Math.PI);

    rotateRef.current = {
      rotating: true,
      type: "image",
      id: img.id,
      centerX,
      centerY,
      lastAngle: startAngle,
      currentRotation: normalizeRotation(img.rotation || 0),
    };
  };

  const textRotatePointerDown = (e, t) => {
    e.stopPropagation();

    suppressNextClickRef.current = true;
    // Keep text selected while rotating
    setSelectedTextId(t.id);
    setActiveTextId?.(t.id);

    // Remove image selection
    setActiveImageId(null);
    setSelectedImageId?.(null);

    setActiveTab?.(t.role ? 'number' : 'text');

    // KEEP your existing pointer capture
    overlayRef.current?.setPointerCapture(e.pointerId);

    const el = textRefs.current[t.id];
    const overlay = overlayRef.current;

    if (!el || !overlay) return;

    const rect = el.getBoundingClientRect();

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const startAngle =
      Math.atan2(
        e.clientY - centerY,
        e.clientX - centerX
      ) * (180 / Math.PI);

    rotateRef.current = {
      rotating: true,
      type: "text",
      id: t.id,
      centerX,
      centerY,
      lastAngle: startAngle,
      currentRotation: normalizeRotation(t.rotation || 0),
    };
  };

  const imagePointerDown = (e, img) => {
    isDraggingRef.current = true;
    pointerIdRef.current = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);

    setActiveTextId(null);
    setSelectedTextId?.(null);

    dragDataRef.current = {
      type: "image",
      imageId: img.id,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: img.x || 0,
      startY: img.y || 0,
    };
  };

  const activePlacement = placementList.find((x) => x.key === activePlacementKey) || null;
  const activeFallbackPlacement = activePlacement || (placementList.length ? placementList[0] : null);

  const normalizeRotation = (deg) => {
    // Wraps any angle into [0, 360) — e.g. 370 -> 10, -30 -> 330
    return ((deg % 360) + 360) % 360;
  };

  const preview = useMemo(() => {
    if (!activeFallbackPlacement) return { overlay: { display: 'none' }, badge: '' };

    const placement = selectedPlacement;
    const method = currentMethod;

    const placementKey = `${placement}-${method}`;

    const sizes = {
      print: { width: (selectedPlacement !== 'left' && selectedPlacement !== 'right') ? (selectedPlacement === 'back') ? '40%' : '50%' : '70px', height: (selectedPlacement !== 'left' && selectedPlacement !== 'right') ? (selectedPlacement === 'back') ? '66%' : '80%' : '70px', borderRadius: 0 },
      embroidery: { width: '70px', height: '70px', borderRadius: 0 },
    };

    const anchors = {
      front: { top: method === 'print' ? '54%' : '37%', left: method === 'print' ? '50.5%' : '64%', translate: '-50%, -50%', right: 0, },
      back: { top: method === 'print' ? '52%' : '30%', left: method === 'print' ? '50%' : '50%', translate: '-50%, -50%', right: undefined },
      left: { top: '32%', left: '50.2%', translate: '-50%, -50%', right: undefined },
      right: { top: '32%', left: '50.2%', translate: '-50%, -50%', right: undefined },
    };

    const a = anchors[placement];
    const s = sizes[method];

    const overlay = {
      width: `${s?.width}`,
      height: `${s?.height}`,
      borderRadius: `${s?.borderRadius}px`,
      top: `${a?.top}`,
      left: `${a?.left}`,
      transform: `translate(${a.translate})`,
      position: 'absolute',
      border: '1px solid rgba(255,255,255,0.92)',
      boxShadow:
        method === 'print'
          ? '0 0 0 1px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(255,255,255,0.25)'
          : '0 0 0 1px rgba(0,0,0,0.08), inset 0 0 0 1px rgba(255,255,255,0.25)',
      background:
        method === 'print'
          ? 'rgba(255,255,255,0.10)'
          : 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(2px)',
    };

    const badge = `${placements.find((p) => p.id === placement)?.label ?? placement} • ${methods.find((m) => m.id === method)?.label ?? method}`;

    return { overlay, badge };
  }, [selectedPlacement, currentMethod, methods, placements]);

  const hasPreview =
    placementMethods[selectedPlacement] &&
    placementMethods[selectedPlacement] !== 'null';

  const textItems =
    (activePlacementKey && placementTexts[activePlacementKey]) ? placementTexts[activePlacementKey] : [];

  const activeText =
    (activeTextId && Array.isArray(textItems))
      ? textItems.find((t) => t.id === activeTextId) || null
      : null;

  const [showResizeError, setShowResizeError] = useState(false);

  const isDraggingRef = useRef(false);

  const pointerIdRef = useRef(null);
  const svgRef = useRef(null);

  const updateTextPosition = (textId, nextX, nextY) => {
    if (!activePlacementKey) return;
    if (!Array.isArray(placementTexts?.[activePlacementKey])) return;
    if (typeof setPlacementTexts !== 'function') return;

    setPlacementTexts((prev) => {
      const items = Array.isArray(prev?.[activePlacementKey]) ? prev[activePlacementKey] : [];
      return {
        ...prev,
        [activePlacementKey]: items.map((t) =>
          t.id === textId
            ? {
              ...t,
              x: nextX,
              y: nextY,
            }
            : t
        ),
      };
    });
  };

  const updateTextSize = (textId, fontSize) => {
    if (!activePlacementKey) return;
    if (!Array.isArray(placementTexts?.[activePlacementKey])) return;
    if (typeof setPlacementTexts !== 'function') return;

    setPlacementTexts((prev) => {
      const items = Array.isArray(prev?.[activePlacementKey]) ? prev[activePlacementKey] : [];
      return {
        ...prev,
        [activePlacementKey]: items.map((t) =>
          t.id === textId
            ? { ...t, fontSize }
            : t
        ),
      };
    });
  };

  const updateTextRotation = (textId, rotation) => {
    if (!activePlacementKey) return;
    if (!Array.isArray(placementTexts?.[activePlacementKey])) return;
    if (typeof setPlacementTexts !== 'function') return;

    setPlacementTexts((prev) => {
      const items = Array.isArray(prev?.[activePlacementKey]) ? prev[activePlacementKey] : [];
      return {
        ...prev,
        [activePlacementKey]: items.map((t) =>
          t.id === textId ? { ...t, rotation } : t
        ),
      };
    });
  };

  const resizeErrorTimeout = useRef(null);

  const lastFitPlainRef = useRef({});

  const prevTransformPropsRef = useRef({});
  const prevImageTransformPropsRef = useRef({});

  useLayoutEffect(() => {
    if (!activePlacementKey || !Array.isArray(textItems)) return;

    let hasUpdate = false;

    textItems.forEach((t) => {
      if (t.curve) return;

      const prev = prevTransformPropsRef.current[t.id];
      const curr = {
        textAlign: t.textAlign,
        skewX: t.skewX,
        skewY: t.skewY,
        rotation: t.rotation,
        flipH: t.flipH,
        flipV: t.flipV,
      };

      const changed = !prev ||
        prev.textAlign !== curr.textAlign ||
        prev.skewX !== curr.skewX ||
        prev.skewY !== curr.skewY ||
        prev.rotation !== curr.rotation ||
        prev.flipH !== curr.flipH ||
        prev.flipV !== curr.flipV;

      if (!changed) return;

      prevTransformPropsRef.current[t.id] = curr;

      const textEl = textRefs.current[t.id];
      if (!textEl) return;

      const w = textEl.offsetWidth;
      const h = textEl.offsetHeight;
      if (!w || !h) return;

      const x = t.x || 0;
      const y = t.y || 0;

      const { x: clampedX, y: clampedY } = clampTextPosition(x, y, t, w, h);

      if (clampedX !== x || clampedY !== y) {
        updateTextPosition(t.id, clampedX, clampedY);
        hasUpdate = true;
      }
    });

    if (hasUpdate) {
      prevTransformPropsRef.current = {};
    }
  }, [textItems, activePlacementKey, updateTextPosition]);

  useLayoutEffect(() => {
    if (!activePlacementKey || !Array.isArray(imageItems)) return;

    let hasUpdate = false;

    imageItems.forEach((img) => {
      const prev = prevImageTransformPropsRef.current[img.id];
      const curr = {
        skewX: img.skewX,
        skewY: img.skewY,
        rotation: img.rotation,
      };

      const changed = !prev ||
        prev.skewX !== curr.skewX ||
        prev.skewY !== curr.skewY ||
        prev.rotation !== curr.rotation;

      if (!changed) return;

      prevImageTransformPropsRef.current[img.id] = curr;

      const el = imageRefs.current[img.id];
      if (!el) return;

      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (!w || !h) return;

      const x = img.x || 0;
      const y = img.y || 0;

      const hasNonZeroTransform = (img.skewX || 0) !== 0 || (img.skewY || 0) !== 0 || (img.rotation || 0) !== 0;
      if (!hasNonZeroTransform) return;

      const { x: clampedX, y: clampedY } = clampImagePosition(x, y, img, w, h);

      if (clampedX !== x || clampedY !== y) {
        updateImagePosition(img.id, clampedX, clampedY);
        hasUpdate = true;
      }
    });

    if (hasUpdate) {
      prevImageTransformPropsRef.current = {};
    }
  }, [imageItems, activePlacementKey, updateImagePosition]);

  useEffect(() => {
    const checkOverflow = () => {
      const overlay = overlayRef.current;

      if (!overlay) {
        setShowResizeError(false);
        return;
      }

      let hasOverflow = false;

      const overlayWidth = overlay.clientWidth;
      const overlayHeight = overlay.clientHeight;

      textItems?.forEach((t) => {
        const textEl = textRefs.current[t.id];

        if (!textEl) return;

        const textWidth = textEl.offsetWidth;
        const textHeight = textEl.offsetHeight;

        const x = t.x || 0;
        const y = t.y || 0;

        const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getTextVisualBounds(t, textWidth, textHeight);

        const isOutside =
          x - offsetLeft < 0 ||
          y - offsetTop < 0 ||
          x + textWidth + offsetRight > overlayWidth ||
          y + textHeight + offsetBottom > overlayHeight;

        if (isOutside) {
          hasOverflow = true;
        }
      });

      imageItems?.forEach((img) => {
        const imgWidth = img.width || 150;
        const imgHeight = img.height || 150;

        const x = img.x || 0;
        const y = img.y || 0;

        const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getImageVisualBounds(img, imgWidth, imgHeight);

        const isOutside =
          x - offsetLeft < 0 ||
          y - offsetTop < 0 ||
          x + imgWidth + offsetRight > overlayWidth ||
          y + imgHeight + offsetBottom > overlayHeight;

        if (isOutside) {
          hasOverflow = true;
        }
      });

      clearTimeout(resizeErrorTimeout.current);

      if (hasOverflow) {
        resizeErrorTimeout.current = setTimeout(() => {
          setShowResizeError(true);
        }, 2000);
      } else {
        setShowResizeError(false);
      }
    };

    checkOverflow();

    return () => {
      clearTimeout(resizeErrorTimeout.current);
    };
  }, [textItems, imageItems, activePlacementKey]);

  useLayoutEffect(() => {
    if (!activePlacementKey || !Array.isArray(textItems)) return;

    textItems.forEach((t) => {
      if (t.curve) return;
      if (t.role) return;

      const textEl = textRefs.current[t.id];
      if (!textEl) return;

      const raw = (t.text || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/br>/gi, '\n')
        .replace(/\\n/g, '\n');
      if (!raw.trim()) return;

      const dims = getOverlayDims();
      if (!dims || dims.width <= 0 || dims.height <= 0) return;

      const x = t.x || 0;
      const y = t.y || 0;
      const textWidth = textEl.offsetWidth;
      const textHeight = textEl.offsetHeight;
      if (!textWidth || !textHeight) return;

      const { offsetLeft, offsetRight, offsetTop, offsetBottom } = getTextVisualBounds(t, textWidth, textHeight);

      const maxWidth = Math.max(1, dims.width - x - offsetRight + offsetLeft);
      const maxHeight = Math.max(1, dims.height - y - offsetBottom + offsetTop);

      if (textWidth <= maxWidth && textHeight <= maxHeight) return;

      const currentFontSize = t.fontSize || 16;
      const scale = Math.min(maxWidth / textWidth, maxHeight / textHeight);
      const nextFontSize = Math.max(8, Math.floor(currentFontSize * scale * 0.98));

      if (nextFontSize >= currentFontSize) return;

      const last = lastFitPlainRef.current[t.id];
      if (last && last.fontSize === nextFontSize && last.text === raw) return;

      lastFitPlainRef.current[t.id] = { fontSize: nextFontSize, text: raw };
      updateTextSize(t.id, nextFontSize);
    });
  }, [textItems, activePlacementKey, updateTextSize]);

  useLayoutEffect(() => {
    if (!activePlacementKey || !Array.isArray(textItems)) return;

    const selectedItem = textItems.find((t) => t.id === activeTextId && t.role);
    if (!selectedItem) return;

    const dims = getOverlayDims();
    if (!dims || dims.width <= 0 || dims.height <= 0) return;

    const lineHeightFactor = 1.25;

    const raw = (selectedItem.text || '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/br>/gi, '\n')
      .replace(/\\n/g, '\n');
    if (!raw.trim()) return;

    const textEl = textRefs.current[selectedItem.id];
    if (!textEl) return;

    const textW = textEl.offsetWidth || 0;
    const textH = textEl.offsetHeight || 0;
    const x = selectedItem.x || 0;
    const y = selectedItem.y || 0;

    const visual = getTextVisualBounds(selectedItem, textW, textH);
    const maxWidth = Math.max(1, dims.width - x - visual.offsetRight + visual.offsetLeft);
    const maxHeight = Math.max(1, dims.height - y - visual.offsetBottom + visual.offsetTop);

    if (textW <= maxWidth && textH <= maxHeight) return;

    const currentFontSize = selectedItem.fontSize || 16;
    const scale = Math.min(maxWidth / Math.max(1, textW), maxHeight / Math.max(1, textH));
    const nextFontSize = Math.max(8, Math.floor(currentFontSize * scale * 0.98));

    if (nextFontSize >= currentFontSize) return;

    const last = lastFitPlainRef.current[selectedItem.id];
    if (last && last.fontSize === nextFontSize && last.text === raw) return;

    lastFitPlainRef.current[selectedItem.id] = { fontSize: nextFontSize, text: raw };

    setPlacementTexts((prev) => {
      const arr = Array.isArray(prev?.[activePlacementKey])
        ? [...prev[activePlacementKey]]
        : [];
      return {
        ...prev,
        [activePlacementKey]: arr.map((t) =>
          t.id === selectedItem.id
            ? { ...t, fontSize: nextFontSize }
            : t
        ),
      };
    });
  }, [textItems, activePlacementKey, updateTextSize, activeTextId]);

  const overlayPointerDown = (e, t = null) => {

    isDraggingRef.current = true;
    pointerIdRef.current = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);

    if (t) {
      setActiveImageId(null);
      setSelectedImageId?.(null);

      setSelectedTextId(t.id);
      setActiveTextId?.(t.id);
      setActiveTab?.(t.role ? 'number' : 'text');
      dragDataRef.current = {
        type: "text",
        textId: t.id,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: t.x || 0,
        startY: t.y || 0,
      };
      return;
    }
  };



  const overlayPointerMove = (e) => {

    if (rotateRef.current.rotating) {

      const { centerX, centerY, lastAngle, currentRotation, type, id } = rotateRef.current;

      const angleNow =
        Math.atan2(e.clientY - centerY, e.clientX - centerX) * (180 / Math.PI);

      // Frame-to-frame delta, unwrapped so a single step never exceeds ±180°.
      // This is what actually prevents the atan2 discontinuity from
      // ever showing up as a jump — each step is small enough that it
      // can never straddle the -180/180 boundary in a way that misfires.
      let delta = angleNow - lastAngle;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;

      const nextRotation = currentRotation + delta;

      // Persist for the next frame — this is what makes rotation
      // continuous across any number of full revolutions.
      rotateRef.current.lastAngle = angleNow;
      rotateRef.current.currentRotation = nextRotation;

      if (type === "text") {
        updateTextRotationNormalized(id, nextRotation);
      } else {
        updateImageRotationNormalized(id, nextRotation);
      }

      return;

    }

    if (resizeRef.current.resizing) {

      // Cancel resize if the pointer has moved outside the overlay
      // const overlayEl = overlayRef.current;
      // if (overlayEl) {
      //   const rect = overlayEl.getBoundingClientRect();
      //   if (
      //     e.clientX < rect.left ||
      //     e.clientX > rect.right ||
      //     e.clientY < rect.top ||
      //     e.clientY > rect.bottom
      //   ) {
      //     resizeRef.current.resizing = false;
      //     overlayEl.releasePointerCapture?.(e.pointerId);
      //     setResizingActiveId(null);
      //     return;
      //   }
      // }

      const dx = e.clientX - resizeRef.current.startX;
      const dy = e.clientY - resizeRef.current.startY;

      if (resizeRef.current.type === "text") {

        const t = textItems.find((item) => item.id === resizeRef.current.textId);
        const x = t?.x || 0;
        const y = t?.y || 0;
        const currentFontSize = resizeRef.current.startFontSize || 16;

        const delta = (dx + dy) / 2;
        let nextFontSize = Math.max(8, currentFontSize + delta * 0.5);

        // Predict the rendered box at the new font size (scaled from the
        // element's current measured size) and cap growth so it can't
        // push the text outside the design area.
        const textEl = textRefs.current[resizeRef.current.textId];
        if (textEl && textEl.offsetWidth > 0 && textEl.offsetHeight > 0) {
          const measuredFontSize = t?.fontSize || currentFontSize;
          const scale = nextFontSize / measuredFontSize;
          const predictedWidth = textEl.offsetWidth * scale;
          const predictedHeight = textEl.offsetHeight * scale;

          const dims = getOverlayDims();
          if (dims) {
            const maxWidth = Math.max(1, dims.width - x);
            const maxHeight = Math.max(1, dims.height - y);

            if (predictedWidth > maxWidth || predictedHeight > maxHeight) {
              const widthScale = maxWidth / textEl.offsetWidth;
              const heightScale = maxHeight / textEl.offsetHeight;
              const maxScale = Math.min(widthScale, heightScale);
              nextFontSize = Math.max(8, measuredFontSize * maxScale);
            }
          }

          // if (t) {
          //   const visualScale = getMaxTextScale(t, textEl.offsetWidth, textEl.offsetHeight);
          //   const visualCapFontSize = Math.max(8, measuredFontSize * visualScale);
          //   if (nextFontSize > visualCapFontSize) {
          //     nextFontSize = visualCapFontSize;
          //   }
          // }
        }

        updateTextSize(resizeRef.current.textId, nextFontSize);
        return;

      }

      const img = imageItems.find((item) => item.id === resizeRef.current.imageId);
      if (!img) return;

      // Use the image's stored aspect ratio so that vertical and
      // horizontal scaling stay locked together — dragging the resize
      // handle stretches the image uniformly in both dimensions.
      const aspectRatio =
        resizeRef.current.startWidth && resizeRef.current.startHeight
          ? resizeRef.current.startWidth / resizeRef.current.startHeight
          : img?.aspectRatio && Number.isFinite(img.aspectRatio) && img.aspectRatio > 0
            ? img.aspectRatio
            : (img?.width && img?.height ? img.width / img.height : 1);

      const direction = resizeRef.current.direction || 'right';
      const startWidth = resizeRef.current.startWidth;
      const startHeight = resizeRef.current.startHeight;
      const startXPos = resizeRef.current.startXPos;
      const startYPos = resizeRef.current.startYPos;
      const MIN_SIZE = 30;

      const startRight = startXPos + startWidth;
      const startBottom = startYPos + startHeight;

      let newWidth = startWidth;
      let newHeight = startHeight;
      let newX = startXPos;
      let newY = startYPos;

      if (direction === 'right' || direction === 'se') {
        newWidth = Math.max(30, startWidth + dx);
        if (direction === 'se') {
          newHeight = Math.max(30, startHeight + dy);
        }
      } else if (direction === 'left') {
        newWidth = Math.max(30, startWidth - dx);
        newX = startXPos + startWidth - newWidth;
      } else if (direction === 'bottom' || direction === 'sw') {
        newHeight = Math.max(30, startHeight + dy);
        if (direction === 'sw') {
          newWidth = Math.max(30, startWidth - dx);
          newX = startXPos + startWidth - newWidth;
        }
      } else if (direction === 'top' || direction === 'ne') {
        newHeight = Math.max(30, startHeight - dy);
        newY = startYPos + startHeight - newHeight;
        if (direction === 'ne') {
          newWidth = Math.max(30, startWidth + dx);
        }
      }

      const dims = getOverlayDims();

      if (!dims) return;
      let finalWidth = newWidth;
      let finalHeight = newHeight;
      let finalX = newX;
      let finalY = newY;

      if (dims) {
        if (direction === "right") {
          finalX = startXPos;

          finalWidth = startWidth + dx;

          const maxWidth = dims.width - startXPos;

          finalWidth = Math.max(
            MIN_SIZE,
            Math.min(finalWidth, maxWidth)
          );
        }

        // LEFT
        else if (direction === "left") {
          // Only move the left edge
          let nextX = startXPos + dx;

          // Don't allow left edge outside print area
          nextX = Math.max(0, nextX);

          // Don't allow left edge to cross right edge
          nextX = Math.min(startRight - MIN_SIZE, nextX);

          finalX = nextX;

          // IMPORTANT:
          // Right edge stays fixed
          finalWidth = startRight - finalX;
        }

        // BOTTOM
        else if (direction === "bottom") {
          finalY = startYPos;

          finalHeight = startHeight + dy;

          const maxHeight = dims.height - startYPos;

          finalHeight = Math.max(
            MIN_SIZE,
            Math.min(finalHeight, maxHeight)
          );
        }

        // TOP
        else if (direction === "top") {
          // Only move the top edge
          let nextY = startYPos + dy;

          // Don't allow top edge outside print area
          nextY = Math.max(0, nextY);

          // Don't allow top edge to cross bottom edge
          nextY = Math.min(startBottom - MIN_SIZE, nextY);

          finalY = nextY;

          // IMPORTANT:
          // Bottom edge stays fixed
          finalHeight = startBottom - finalY;
        }

        // SOUTH EAST
        // SOUTH EAST
        else if (direction === "se") {
          finalX = startXPos;
          finalY = startYPos;

          const maxWidth = Math.max(MIN_SIZE, dims.width - startXPos);
          const maxHeight = Math.max(MIN_SIZE, dims.height - startYPos);

          /*
           * Keep the original image ratio.
           *
           * Horizontal movement:
           *   width = startWidth + dx
           *
           * Vertical movement:
           *   width = (startHeight + dy) * aspectRatio
           *
           * Use whichever movement represents the larger resize intent.
           * This means dragging horizontally OR vertically keeps the
           * exact same aspect ratio.
           */
          const widthFromX = startWidth + dx;
          const widthFromY = (startHeight + dy) * aspectRatio;

          let targetWidth;

          if (Math.abs(dx) >= Math.abs(dy)) {
            targetWidth = widthFromX;
          } else {
            targetWidth = widthFromY;
          }

          // Keep width inside the print area.
          targetWidth = Math.max(
            MIN_SIZE,
            Math.min(targetWidth, maxWidth)
          );

          // Always derive height from width.
          let targetHeight = targetWidth / aspectRatio;

          /*
           * If the calculated height is larger than the print area,
           * reduce BOTH dimensions together so the aspect ratio
           * is never broken.
           */
          if (targetHeight > maxHeight) {
            targetHeight = maxHeight;
            targetWidth = targetHeight * aspectRatio;
          }

          // Final safety check.
          targetWidth = Math.max(
            MIN_SIZE,
            Math.min(targetWidth, maxWidth)
          );

          targetHeight = targetWidth / aspectRatio;

          // If minimum width causes height to exceed the available area,
          // use the maximum valid height instead.
          if (targetHeight > maxHeight) {
            targetHeight = maxHeight;
            targetWidth = targetHeight * aspectRatio;
          }

          finalWidth = targetWidth;
          finalHeight = targetHeight;
        }

        // SOUTH WEST
        else if (direction === "sw") {
          // LEFT side moves
          let nextX = startXPos + dx;

          nextX = Math.max(0, nextX);
          nextX = Math.min(startRight - MIN_SIZE, nextX);

          finalX = nextX;

          // Right edge stays fixed
          finalWidth = startRight - finalX;

          // BOTTOM moves
          finalHeight = startHeight + dy;

          finalHeight = Math.max(
            MIN_SIZE,
            Math.min(finalHeight, dims.height - startYPos)
          );
        }

        // NORTH EAST
        else if (direction === "ne") {
          // RIGHT side moves
          finalWidth = startWidth + dx;

          finalWidth = Math.max(
            MIN_SIZE,
            Math.min(finalWidth, dims.width - startXPos)
          );

          // TOP side moves
          let nextY = startYPos + dy;

          nextY = Math.max(0, nextY);
          nextY = Math.min(startBottom - MIN_SIZE, nextY);

          finalY = nextY;

          // Bottom edge stays fixed
          finalHeight = startBottom - finalY;
        }

        finalX = Math.max(0, finalX);
        finalY = Math.max(0, finalY);
      }
      finalWidth = Math.max(
        MIN_SIZE,
        Math.min(finalWidth, dims.width - finalX)
      );

      finalHeight = Math.max(
        MIN_SIZE,
        Math.min(finalHeight, dims.height - finalY)
      );

      updateImageSize(resizeRef.current.imageId, finalWidth, finalHeight);
      updateImagePosition(resizeRef.current.imageId, finalX, finalY);

      return;

    }

    if (dragDataRef.current.type === "image") {

      const dx = e.clientX - dragDataRef.current.startClientX;
      const dy = e.clientY - dragDataRef.current.startClientY;

      const img = imageItems.find((item) => item.id === dragDataRef.current.imageId);
      const width = img?.width || 150;
      const height = img?.height || 150;

      const rawX = dragDataRef.current.startX + dx;
      const rawY = dragDataRef.current.startY + dy;

      const { x, y } = clampImagePosition(rawX, rawY, img, width, height);

      updateImagePosition(dragDataRef.current.imageId, x, y);

      return;

    }

    if (!isDraggingRef.current) return;
    if (!overlayRef.current) return;
    if (pointerIdRef.current != null && e.pointerId !== pointerIdRef.current) return;

    const dx = e.clientX - dragDataRef.current.startClientX;
    const dy = e.clientY - dragDataRef.current.startClientY;

    const rawX = dragDataRef.current.startX + dx;
    const rawY = dragDataRef.current.startY + dy;

    if (dragDataRef.current.textId) {
      const textEl = textRefs.current[dragDataRef.current.textId];
      const width = textEl?.offsetWidth || 0;
      const height = textEl?.offsetHeight || 0;
      const textItem = textItems.find((item) => item.id === dragDataRef.current.textId);

      const { x, y } = textItem
        ? clampTextPosition(rawX, rawY, textItem, width, height)
        : clampPosition(rawX, rawY, width, height);
      updateTextPosition(dragDataRef.current.textId, x, y);
      return;
    }

    if (dragDataRef.current.type === "text") {
      const textEl = textRefs.current[dragDataRef.current.textId];
      const width = textEl?.offsetWidth || 0;
      const height = textEl?.offsetHeight || 0;
      const textItem = textItems.find((item) => item.id === dragDataRef.current.textId);

      const { x, y } = textItem
        ? clampTextPosition(rawX, rawY, textItem, width, height)
        : clampPosition(rawX, rawY, width, height);
      updateTextPosition(dragDataRef.current.textId, x, y);
    }
    else if (dragDataRef.current.type === "method") {

      setMethodPositions(prev => ({
        ...prev,
        [activePlacementKey]: {
          x: rawX,
          y: rawY,
        }
      }));

    }
  };

  const overlayCenterRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || typeof setOverlayCenter !== 'function') return;

    const updateCenter = () => {
      const w = overlay.clientWidth;
      const h = overlay.clientHeight;
      const newCenter = { x: Math.round(w / 2), y: Math.round(h / 2) };

      if (
        overlayCenterRef.current.x !== newCenter.x ||
        overlayCenterRef.current.y !== newCenter.y
      ) {
        overlayCenterRef.current = newCenter;
        setOverlayCenter(newCenter);
      }
    };

    updateCenter();

    const ro = new ResizeObserver(() => {
      updateCenter();
    });
    ro.observe(overlay);

    return () => ro.disconnect();
  }, [activePlacementKey, setOverlayCenter]);

  const overlayPointerUp = (e) => {
    if (
      pointerIdRef.current != null &&
      e.pointerId !== pointerIdRef.current
    ) {
      return;
    }

    isDraggingRef.current = false;
    resizeRef.current.resizing = false;
    rotateRef.current.rotating = false;

    pointerIdRef.current = null;

    setResizingActiveId(null);

    // IMPORTANT:
    // Restore selection after resize.
    const selection = resizingSelectionRef.current;

    if (selection) {
      if (selection.type === "image") {
        setActiveImageId(selection.id);
        setSelectedImageId?.(selection.id);

        setActiveTextId?.(null);
        setSelectedTextId?.(null);
      }

      if (selection.type === "text") {
        setActiveTextId?.(selection.id);
        setSelectedTextId(selection.id);

        setActiveImageId(null);
        setSelectedImageId?.(null);
      }
    }

    dragDataRef.current = {
      textId: null,
      startClientX: 0,
      startClientY: 0,
      startX: 0,
      startY: 0,
    };
  };

  // const measureSvgText = (text, fontSize, fontFamily, fontWeight = "400") => {
  //   const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  //   const textNode = document.createElementNS("http://www.w3.org/2000/svg", "text");

  //   textNode.setAttribute("font-size", fontSize);
  //   textNode.setAttribute("font-family", fontFamily);
  //   textNode.setAttribute("font-weight", fontWeight);
  //   textNode.textContent = text;

  //   svg.appendChild(textNode);
  //   document.body.appendChild(svg);

  //   const box = textNode.getBBox();

  //   document.body.removeChild(svg);

  //   return {
  //     width: box.width,
  //     height: box.height,
  //   };
  // };



  return (
    <div
      className="placement-container"
    >
      <div className="placement-content">
        {/* Left Sidebar */}
        <div className="placement-sidebar" style={{ minWidth: 220 }}>

          <div className="placement-buttons">
            {placements.map((placement) => {

              return (
                <div
                  key={placement.id}
                  className={`placement-card ${activeView === placement.id ? "active" : ""
                    }`}
                  onClick={() => {
                    setSelectedPlacement(placement.id);
                    setActiveView(placement.id);

                    const placementItem = placementList.find(
                      (item) => item.placement === placement.id
                    );

                    if (placementItem) {
                      setActivePlacementKey(placementItem.key);
                    }
                  }}
                >
                  <div className="placement-card-header">
                    <div className="placement-card-header-content">
                      <img
                        src={shirtImages[placement?.id] || placement?.icon}
                        height={40}
                        width={25}
                        style={{ verticalAlign: 'middle', objectFit: 'contain' }}
                        alt={placement?.label}
                      />
                      <div>

                        <span style={{ verticalAlign: 'middle' }}>{placement.label}</span>
                        <div className="selected-methods">
                          {placementList
                            .filter((item) => item.placement === placement.id)
                            .map((item) => (
                              <div
                                key={item.key}
                                className={`selected-method-item ${activePlacementKey === item.key ? 'active' : ''
                                  }`}
                                onClick={() => {
                                  setSelectedPlacement(item.placement);
                                  setActiveView(item.placement);

                                  setActivePlacementKey(item.key);
                                }}
                              >
                                <span>
                                  {methods.find((m) => m.id === item.method)?.label}
                                </span>

                                <button
                                  type="button"
                                  className="remove-method-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemovePlacement(item.key);
                                  }}
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                    {/* Selected methods list */}
                  </div>

                  {
                    placementList?.filter((item) => item?.placement === placement?.id)?.length <= 0 &&
                    <>
                      {selectedPlacement === placement.id && (
                        <div className="placement-card-methods">
                          <div className="method-popup">
                            {methods.map((method) => (
                              <label key={method.id}>
                                <input
                                  type="radio"
                                  checked={currentMethod === method.id}
                                  onChange={() => handleAddPlacement(placement.id, method.id)}
                                />
                                {method.label}
                              </label>
                            ))}
                          </div>

                        </div>
                      )}
                    </>
                  }

                </div>
              );
            })}
          </div>
        </div>

        {/* Right Preview */}
        <div className="preview-section" onClick={() => {
          setActiveTextId?.(null);
          setSelectedTextId?.(null);
          setActiveImageId(null);
          setSelectedImageId?.(null);
        }}>
          <div
            style={{
              background: "#fee2e2",
              color: "#dc2626",
              border: "1px solid #fca5a5",
              padding: "10px 14px",
              borderRadius: "8px",
              marginBottom: "12px",
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "center",
              opacity: !showResizeError && '0'
            }}
          >
            Resize your artwork to fit within the design area.
          </div>
          <div className="tshirt-container">
            <div className="tshirt-preview" >
              <div
                style={{
                  width: '100%',
                  borderRadius: 20,
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={shirtImages[activeView]}
                  alt={activeView}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    transition: 'all 0.3s ease',
                  }}
                />
              </div>
              {hasPreview && (
                <>
                  {/* Preview Area */}
                  <div
                    ref={overlayRef}
                    style={{
                      ...preview.overlay,
                      transform: `${preview.overlay.transform} translate(${methodPos.x}px, ${methodPos.y}px)`,
                      position: 'absolute',
                      cursor: currentMethod === 'embroidery' ? 'all-scroll' : '',
                      opacity: isRemoveWhiteLoader ? 0.6 : 1
                    }}
                    onPointerDown={handleMethodPointerDown}
                    onPointerMove={(e) => {
                      handleMethodPointerMove(e);
                      overlayPointerMove(e);
                    }}
                    onPointerUp={(e) => {
                      handleMethodPointerUp(e);
                      overlayPointerUp(e);
                    }}
                    onPointerCancel={overlayPointerUp}
                    onClick={(e) => {
                      // Ignore the click generated after resize
                      if (suppressNextClickRef.current) {
                        suppressNextClickRef.current = false;

                        // IMPORTANT:
                        // Stop the event from reaching preview-section
                        e.stopPropagation();
                        return;
                      }

                      // Only deselect when directly clicking empty print area
                      if (e.target !== e.currentTarget) {
                        return;
                      }

                      setActiveTextId?.(null);
                      setSelectedTextId?.(null);
                      setActiveImageId(null);
                      setSelectedImageId?.(null);

                      e.stopPropagation();
                    }}
                  >
                    {currentMethod === 'embroidery' &&
                      (activePlacementKey?.startsWith('front-') || activePlacementKey?.startsWith('back-')) && (
                        <div
                          onPointerDown={handleMoveHandlePointerDown}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: 'absolute',
                            top: -24,
                            right: -10,
                            transform: 'translateX(-50%)',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#2563eb',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'grab',
                            fontSize: 12,
                            zIndex: 40,
                            touchAction: 'none',
                            userSelect: 'none',
                          }}
                          title="Drag to move"
                        >
                          ✛
                        </div>
                      )}

                    {Array.isArray(imageItems) && imageItems.map((imgItem) => (
                      <div
                        key={imgItem.id}
                        style={{
                          position: "absolute",
                          left: imgItem.x || 0,
                          top: imgItem.y || 0,
                          width: imgItem.width || 60,
                          height: imgItem.height || 60,
                          // no rotation here — this wrapper stays fixed
                        }}
                      >
                        <div
                          ref={(el) => { if (el) imageRefs.current[imgItem.id] = el; }}
                          style={{
                            position: "absolute",
                            inset: 0,
                            transform: `rotate(${imgItem.rotation || 0}deg) skewX(${imgItem.skewX || 0}deg) skewY(${imgItem.skewY || 0}deg)`,
                            border: activeImageId === imgItem.id ? "2px dashed #666" : "2px solid transparent",
                            boxSizing: "border-box",
                            cursor: "move",
                            opacity: imgItem.opacity ?? 1,
                            zIndex: activeImageId === imgItem.id ? 10 : 1
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveImageId(imgItem.id);
                            setActiveTextId(null);
                            setSelectedTextId(null);
                            setActiveTab?.('images');
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();

                            setActiveImageId(imgItem.id);
                            setSelectedImageId?.(imgItem.id);

                            setActiveTextId?.(null);
                            setSelectedTextId?.(null);

                            setActiveTab?.('images');

                            isDraggingRef.current = true;
                            pointerIdRef.current = e.pointerId;

                            e.currentTarget.setPointerCapture(e.pointerId);

                            dragDataRef.current = {
                              type: "image",
                              imageId: imgItem.id,
                              startClientX: e.clientX,
                              startClientY: e.clientY,
                              startX: imgItem.x || 0,
                              startY: imgItem.y || 0
                            };
                          }}
                        >
                          <img
                            src={imgItem.imageDataUrl}
                            draggable={false}
                            style={{
                              width: "100%",
                              height: "100%",
                              pointerEvents: "none",
                              userSelect: "none",
                              filter: buildImageFilter(imgItem)
                            }}
                          />

                          {
                            activeImageId === imgItem.id && (
                              <>
                                <div
                                  onPointerDown={(e) => {
                                    e.stopPropagation();

                                    setActiveImageId(imgItem.id);
                                    setActiveTab?.('images');

                                    overlayPointerDown(e);

                                    dragDataRef.current = {
                                      type: "image",
                                      imageId: imgItem.id,
                                      startClientX: e.clientX,
                                      startClientY: e.clientY,
                                      startX: imgItem.x || 0,
                                      startY: imgItem.y || 0
                                    };
                                  }}
                                  style={{
                                    background: "#2563eb",
                                    position: "absolute",
                                    left: -7,
                                    top: 0,
                                    height: 16,
                                    width: 16,
                                    transform: `translateY(-50%) rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    zIndex: 9,
                                    fontSize: 10
                                  }}
                                >
                                  <Move size={14} color='white' />
                                </div>
                                <div
                                  onPointerDown={(e) => resizePointerDown(e, imgItem, 'right')}
                                  style={{
                                    width: 12,
                                    height: 12,
                                    background: "#2563eb",
                                    position: "absolute",
                                    right: -7,
                                    top: '50%',
                                    transform: `translateY(-50%) rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                    cursor: "ew-resize",
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    borderRadius: '50%'
                                  }}
                                />

                                <div
                                  onPointerDown={(e) => resizePointerDown(e, imgItem, 'left')}
                                  style={{
                                    width: 12,
                                    height: 12,
                                    background: "#2563eb",
                                    position: "absolute",
                                    left: -7,
                                    top: '50%',
                                    transform: `translateY(-50%) rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                    cursor: "ew-resize",
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    borderRadius: '50%'
                                  }}
                                />

                                {/* <div
                                  onPointerDown={(e) => resizePointerDown(e, imgItem, 'bottom')}
                                  style={{
                                    width: 14,
                                    height: 14,
                                    background: "#2563eb",
                                    position: "absolute",
                                    bottom: -7,
                                    left: '50%',
                                    transform: `translateX(-50%) rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                    cursor: "ns-resize",
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    borderRadius: '50%'
                                  }}
                                /> */}

                                <div
                                  onPointerDown={(e) => resizePointerDown(e, imgItem, 'top')}
                                  style={{
                                    width: 12,
                                    height: 12,
                                    background: "#2563eb",
                                    position: "absolute",
                                    top: -7,
                                    left: '50%',
                                    transform: `translateX(-50%) rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                    cursor: "ns-resize",
                                    touchAction: 'none',
                                    userSelect: 'none',
                                    borderRadius: '50%'
                                  }}
                                />

                                <div
                                  onPointerDown={(e) => resizePointerDown(e, imgItem, 'se')}
                                  style={{
                                    width: 14,
                                    height: 14,
                                    background: "#2563eb",
                                    position: "absolute",
                                    right: -7,
                                    bottom: -7,
                                    cursor: "nwse-resize",
                                    transform: `rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                    touchAction: 'none',
                                    userSelect: 'none',
                                  }}
                                >
                                  <svg xmlns="http://w3.org" viewBox="0 0 100 140" width="14" height="20">
                                    <path d="M 20 80 L 80 80 L 80 20" fill="none" stroke="white" stroke-width="10" stroke-linecap="square" />
                                    <path d="M 68 80 L 68 68 L 80 68" fill="none" stroke="white" stroke-width="4" />
                                  </svg>
                                </div>

                                <button
                                  onPointerDown={(e) => imageRotatePointerDown(e, imgItem)}
                                  style={{
                                    position: 'absolute',
                                    right: -7,
                                    top: -7,
                                    transform: 'translateX(-50%)',
                                    cursor: 'grab',
                                    height: 18,
                                    width: 18,
                                    border: 0,
                                    background: '#2563EB',
                                    textAlign: 'center',
                                    fontSize: 15,
                                    color: 'white',
                                    opacity: 1,
                                    WebkitTextStroke: undefined,
                                    textShadow: 'none',
                                    transform: `rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                  }}
                                >
                                  ⟳
                                </button>
                                <div
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeImage(imgItem.id);
                                  }}
                                  style={{
                                    width: 16,
                                    height: 16,
                                    background: "#2563EB",
                                    color: "#fff",
                                    fontSize: 10,
                                    lineHeight: "16px",
                                    textAlign: "center",
                                    position: "absolute",
                                    bottom: -7,
                                    left: -7,
                                    cursor: "pointer",
                                    userSelect: "none",
                                    WebkitTextStroke: 0,
                                    transform: `rotate(${(imgItem.rotation || 0) * -1}deg) skewX(${(imgItem.skewX || 0) * -1}deg) skewY(${(imgItem.skewY || 0) * -1}deg)`,
                                  }}
                                >
                                  <Trash2 size={14} color='white' />
                                </div>
                              </>
                            )}
                        </div>

                        {/* Badge now lives in the unrotated wrapper — sibling, not child, of the rotated box */}
                        {activeImageId === imgItem.id && (
                          <InchesBadge
                            widthPx={imgItem.width || 60}
                            heightPx={imgItem.height || 60}
                            active={resizingActiveId === imgItem.id}
                            isImage={true}
                            imageSrc={imgItem?.imageDataUrl}
                            setIsRemoveWhiteLoader={setIsRemoveWhiteLoader}
                          />
                        )}
                      </div>
                    ))}

                    {Array.isArray(textItems) && textItems.map((t) => {
                      const isActive = t.id === selectedTextId;
                      const x = typeof t.x === 'number' ? t.x : 0;
                      const y = typeof t.y === 'number' ? t.y : 0;
                      const displayText = (t.text || '')
                        .replace(/<br\s*\/?>/gi, '\n')
                        .replace(/<\/br>/gi, '\n')
                        .replace(/\\n/g, '\n');

                      const w = textRefs.current[t.id]?.offsetWidth || 0;
                      const h = textRefs.current[t.id]?.offsetHeight || 0;

                      return (
                        <React.Fragment key={t.id}>
                          {t?.text?.length > 0 && (
                            <div
                              style={{
                                position: 'absolute',
                                left: x,
                                top: y,
                                zIndex: 99999,
                              }}
                            >
                              <div
                                ref={(el) => { if (el) textRefs.current[t.id] = el; }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveImageId(null);
                                  setSelectedImageId?.(null);
                                  setActiveTextId?.(t.id);
                                  setSelectedTextId?.(t.id);
                                  setActiveTab?.(t.role ? 'number' : 'text');
                                }}
                                onPointerDown={(e) => { e.stopPropagation(); overlayPointerDown(e, t); }}
                                onPointerMove={(e) => e.preventDefault()}
                                style={{
                                  display: 'inline-block',
                                  position: 'relative',
                                  transform: `rotate(${t.rotation || 0}deg) scaleX(${t.flipH ? -1 : 1}) scaleY(${t.flipV ? -1 : 1}) skewX(${t.skewX || 0}deg) skewY(${t.skewY || 0}deg)`,

                                  whiteSpace: 'pre',
                                  textAlign: t.textAlign || "left",
                                  textTransform: t.textTransform || "none",
                                  fontSize: t.fontSize || 16,
                                  fontFamily: t.fontFamily || 'Arial',
                                  color: t.color || '#ffffff',
                                  fontWeight: t.bold ? '700' : '400',
                                  fontStyle: t.italic ? 'italic' : 'normal',
                                  textDecoration: t.underline ? 'underline' : 'none',
                                  cursor: isActive ? 'grab' : 'pointer',
                                  borderRadius: 0,
                                  border: isActive ? '2px dashed #666' : '2px solid transparent',
                                  background: 'transparent',
                                  userSelect: 'none',
                                  touchAction: 'none',
                                  wordWrap: 'break-word',
                                  textWrap: 'auto',
                                  opacity: t.opacity ?? 1,
                                  WebkitTextStroke: t.strokeWidth ? `${t.strokeWidth}px ${t.strokeColor || '#000000'}` : undefined,
                                  textShadow: (t.shadowOffsetX || t.shadowOffsetY)
                                    ? `${t.shadowOffsetX || 0}px ${t.shadowOffsetY || 0}px 4px ${t.shadowColor || '#000000'}`
                                    : 'none',
                                }}
                              >
                                {(t.curve !== undefined && t.curve) ? (
                                  <CurvedText
                                    t={t}
                                    displayText={displayText}
                                    maxWidth={(getOverlayDims()?.width || 0) - x}
                                    maxHeight={(getOverlayDims()?.height || 0) - y}
                                    onFitFontSize={updateTextSize}
                                    setBoxSize={setBoxSize}
                                    disableFit={resizingActiveId === t.id}
                                  />
                                ) : (
                                  <>
                                    {
                                      displayText.split('\n').map((line, index) => (
                                        <React.Fragment key={index}>
                                          {line}
                                          {index < displayText.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                      ))
                                    }
                                  </>
                                )}
                                {isActive && (
                                  <>
                                    <div
                                      onPointerDown={(e) => textResizePointerDown(e, t)}
                                      style={{
                                        width: 14,
                                        height: 14,
                                        background: "#2563eb",
                                        position: "absolute",
                                        right: -7,
                                        bottom: -7,
                                        cursor: "nwse-resize",
                                        transform: `rotate(${(t.rotation || 0) * -1}deg) scaleX(${1}) scaleY(${1}) skewX(${(t.skewX || 0) * -1}deg) skewY(${(t.skewY || 0) * -1}deg)`,
                                      }}
                                    >
                                      <svg xmlns="http://w3.org" viewBox="0 0 100 100" width="14" height="14" style={{
                                        position: "absolute",
                                        right: 0,
                                        bottom: 0,
                                        cursor: "nwse-resize",
                                        transform: `rotate(${(t.rotation || 0) * -1}deg) scaleX(${1}) scaleY(${1}) skewX(${(t.skewX || 0) * -1}deg) skewY(${(t.skewY || 0) * -1}deg)`,
                                      }}>
                                        <path d="M 20 80 L 80 80 L 80 20" fill="none" stroke="white" stroke-width="10" stroke-linecap="square" />
                                        <path d="M 68 80 L 68 68 L 80 68" fill="none" stroke="white" stroke-width="4" />
                                      </svg>
                                    </div>

                                    <div
                                      onPointerDown={(e) => textRotatePointerDown(e, t)}
                                      style={{
                                        position: 'absolute',
                                        top: -24,
                                        right: '-7px',
                                        top: '-7px',
                                        cursor: 'grab',
                                        height: 18,
                                        width: 18,
                                        border: 0,
                                        background: '#2563EB',
                                        textAlign: 'center',
                                        fontSize: 15,
                                        color: 'white',
                                        opacity: 1,
                                        WebkitTextStroke: 0,
                                        textShadow: 'none',
                                        transform: `rotate(${(t.rotation || 0) * -1}deg) scaleX(1) scaleY(1) skewX(${(t.skewX || 0) * -1}deg) skewY(${(t.skewY || 0) * -1}deg)`,
                                      }}
                                    >
                                      ⟳
                                    </div>
                                    <div
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeText(t.id);
                                      }}
                                      style={{
                                        width: 16,
                                        height: 16,
                                        background: "#2563EB",
                                        color: "#fff",
                                        fontSize: 10,
                                        lineHeight: "16px",
                                        textAlign: "center",
                                        position: "absolute",
                                        bottom: -7,
                                        left: -7,
                                        cursor: "pointer",
                                        userSelect: "none",
                                        WebkitTextStroke: 0,
                                        transform: `rotate(${(t.rotation || 0) * -1}deg) scaleX(1) scaleY(1) skewX(${(t.skewX || 0) * -1}deg) skewY(${(t.skewY || 0) * -1}deg)`,
                                      }}
                                    >
                                      <Trash2 size={14} color='white' />
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Badge is a sibling of the rotated text — stays flat, in place */}
                              {isActive && (
                                <InchesBadge
                                  widthPx={w}
                                  heightPx={h}
                                  active={resizingActiveId === t.id}
                                />
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </>)}
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default TShirtPlacement;